import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react";
import { synthesizeSharePayload } from "@karasu-tools/core";
import { useEditorWidth } from "../hooks/useEditorWidth.js";
import { useLatestRef } from "../hooks/useLatestRef.js";
import type { SharePayload } from "../utils/inline-share.js";
import { SnapshotManager } from "../fs/snapshot-manager.js";
import { EditArea } from "./EditArea.js";
import { EditPane } from "./EditPane.js";
import { OutlineView } from "./OutlineView.js";
import { PreviewColumn } from "./PreviewColumn.js";
import { downloadSvg } from "../utils/download-svg.js";
import { downloadDrawio } from "../utils/download-drawio.js";
import { useAppContext } from "../state/app-context.js";
import { PreviewProvider, groupByAxis } from "../state/preview-context.js";
import { useAppViews } from "../hooks/useAppViews.js";
import { useSnapshotAutoCapture } from "../hooks/useSnapshotAutoCapture.js";
import { useBreadcrumbs } from "../hooks/useBreadcrumbs.js";
import { useJumpToEditor } from "../hooks/useJumpToEditor.js";
import { useCrossNavigation } from "../hooks/useCrossNavigation.js";
import { useViewSvg } from "../hooks/useViewSvg.js";
import { useTheme } from "../theme/index.js";
import { useStyleSource } from "../hooks/useStyleSource.js";
import { useEditorDocument } from "../hooks/useEditorDocument.js";
import { useEdgeDirectionWriter } from "../hooks/useEdgeDirectionWriter.js";
import { useOutline } from "../hooks/useOutline.js";
import { usePreviewContextValue } from "../hooks/usePreviewContextValue.js";
import { useOrgDisplayMode } from "../hooks/useOrgDisplayMode.js";
import { DiffModeBanner } from "./DiffModeBanner.js";
import { DiagramViewShortcuts } from "./DiagramViewShortcuts.js";
import { PreviewFocusShortcut } from "./PreviewFocusShortcut.js";
import { displayRootFor } from "../utils/diagnostic-location.js";
import type { NodeMetadata } from "@karasu-tools/core";

const NO_NODE_METADATA_BY_PATH: Map<string, NodeMetadata> = new Map();

interface AppShellProps {
  entryPath: string | null;
  /**
   * Sidebar content that is always visible — not affected by the collapse toggle.
   * Use for persistent controls like project selectors that should remain accessible
   * even when the sidebar is collapsed.
   */
  sidebarHeaderContent?: ReactNode;
  /** Sidebar content that collapses when the user clicks the Collapse button. */
  sidebarContent?: ReactNode;
  hideEditor?: boolean;
  recompileRef?: RefObject<(() => void) | null>;
  /**
   * When set, the diff banner renders a "View pasted" button that invokes
   * this callback to re-display the pasted blob (Issue #739).
   */
  onViewPasted?: () => void;
  /**
   * Restores the open file when the URL hash changes (browser back/forward
   * or initial deep-link). ProjectMode passes `selectFile`; modes without
   * per-file navigation (Serve/Memory) omit this. Issue #811.
   */
  onFileChange?: (path: string) => Promise<unknown> | void;
  /**
   * A host that owns the editor (the VS Code webview, #3311) takes the jump to a
   * node's definition: it gets the node and the file that defines it, and the
   * detail panel's "Jump to editor" and Cmd/Ctrl+click are offered. Without it,
   * the jump goes to the app's own editor, and an editor-less mode has none.
   */
  onRevealNode?: (nodeId: string, filePath: string) => void;
  /** The host cannot write files yet: GUI writes (edge direction) are not offered. */
  readOnly?: boolean;
  /**
   * Bumped by a host whose files change behind `fs` (the VS Code webview), so
   * the imported style sheets are read again (#3311).
   */
  sourceRevision?: number;
  /**
   * The directory diagnostics show other files relative to, for a host that
   * mounts a whole directory without a project (serve, #3258). Without it,
   * the project root, else the entry's directory.
   */
  displayRoot?: string;
}

/**
 * AppShell — shared layout and logic for all app modes.
 *
 * View compilation, navigation, breadcrumbs, editor-jump, and cross-view
 * navigation live in dedicated hooks (`useAppViews`, `useBreadcrumbs`,
 * `useJumpToEditor`, `useCrossNavigation`). The editor document lifecycle,
 * GUI edge-direction writes, the outline, and the preview-context assembly are
 * likewise extracted (`useEditorDocument`, `useEdgeDirectionWriter`,
 * `useOutline`, `usePreviewContextValue` — #1541). AppShell is a thin
 * orchestrator that wires them to `PreviewColumn` and `EditArea`.
 *
 * Mode-specific concerns (initialization, project management, sidebar content)
 * are handled by the parent wrapper components.
 */
export function AppShell({
  entryPath,
  sidebarHeaderContent,
  sidebarContent,
  hideEditor,
  recompileRef,
  onViewPasted,
  onFileChange,
  onRevealNode,
  readOnly,
  sourceRevision,
  displayRoot: hostDisplayRoot,
}: AppShellProps) {
  const { state, dispatch, fs } = useAppContext();
  const {
    fileContent,
    viewPath,
    activeView,
    selectedDeployBlockId,
    highlightedNodeId,
    highlightAttribute,
    displayMode,
    currentFilePath,
    currentProject,
    compareSource,
    diffSwapped,
  } = state;

  const projectRoot = currentProject?.rootPath ?? null;
  const snapshotManager = useMemo(
    () => (projectRoot ? new SnapshotManager(fs, projectRoot) : null),
    [fs, projectRoot],
  );

  const [isAllLayersOpen, setIsAllLayersOpen] = useState(false);
  const [previewFocused, setPreviewFocused] = useState(false);
  // The org tab's modes — grid / Tree View / the derived team-dependency graph
  // (#2636) — are one value, so "two modes at once" is not a state this can
  // reach. The hook projects the booleans the permalink and share paths already
  // address Tree View by.
  const {
    isOrgTreeViewOpen,
    setOrgTreeView: setIsOrgTreeViewOpen,
    toggleOrgTreeView,
    isTeamDependenciesOpen,
    toggleTeamDependencies,
  } = useOrgDisplayMode();
  // Entity sub-mode: while drilled into a domain in the system view, swap the
  // usecase view for the domain's entity view. Mirrors `isOrgTreeViewOpen`.
  // Intentionally sticky across drills (like Org Tree View): the flag is a
  // display mode, and `PreviewColumn` gates the actual entity view on
  // `hasEntityView`, so it is inert on domains/levels without an entity view.
  // A sticky flag is also what lets the deep-permalink restore (`#krs-entity-…`)
  // set it independently of the drill path without a reset racing the restore.
  const [isEntityViewOpen, setIsEntityViewOpen] = useState(false);

  // The SVG diagram follows the app's effective theme so the rendered
  // diagram matches the chrome (ADR-1470 left SVG out of scope;
  // Issue #1479 closes that gap). `effectiveTheme` is the concrete
  // light/dark — `ThemeProvider` has already resolved `"system"`.
  const { effectiveTheme } = useTheme();

  const views = useAppViews({
    entryPath,
    fs,
    viewPath,
    activeView,
    selectedDeployBlockId,
    highlightedNodeId,
    displayMode,
    theme: effectiveTheme,
    currentFilePath,
    dispatch,
    isOrgTreeViewOpen,
    setIsOrgTreeViewOpen,
    isTeamDependenciesOpen,
    isEntityViewOpen,
    setIsEntityViewOpen,
    compareSource,
    snapshotManager,
    projectRoot,
    swapped: diffSwapped,
    onFileChange,
  });
  const { recompile, navigateViewPath, navigateActiveView } = views;

  useSnapshotAutoCapture(snapshotManager, projectRoot, currentFilePath, fileContent);

  // Expose recompile to parent via ref (used by ServeModeApp for SSE-driven
  // updates). Assigned after commit rather than during render: the ref belongs
  // to the parent, and a render that React discards or replays must not leave
  // the parent holding a callback from a tree that never mounted.
  useEffect(() => {
    if (!recompileRef) return;
    recompileRef.current = recompile;
    return () => {
      recompileRef.current = null;
    };
  }, [recompileRef, recompile]);

  const { breadcrumbItems, orgBreadcrumbItems, scopeLabel } = useBreadcrumbs({
    resolvedSystems: views.system.resolvedSystems,
    organizations: views.org.organizations,
    viewPath,
    activeView,
    deployBlocks: views.deploy.deployBlocks,
    selectedDeployBlockId,
  });

  const { handleEditorReady, handleJumpToEditor } = useJumpToEditor({
    nodeFileIndex: views.system.nodeFileIndex,
    currentFilePath,
    fileContent,
    fs,
    dispatch,
  });

  // The defining file is looked up here, not by the host: the index is the
  // compile's, and the open file is the fallback for a node the index lacks.
  const nodeFileIndex = views.system.nodeFileIndex;
  const revealNode = useMemo(
    () =>
      onRevealNode
        ? (nodeId: string) => {
            const filePath = nodeFileIndex.get(nodeId) ?? currentFilePath;
            if (filePath) onRevealNode(nodeId, filePath);
          }
        : undefined,
    [onRevealNode, nodeFileIndex, currentFilePath],
  );

  const nav = useCrossNavigation({
    dispatch,
    teamPathIndex: views.teamPathIndex,
    orgNodePathIndex: views.org.nodePathIndex,
    navigateViewPath,
  });

  const nodeMetadata =
    activeView === "deploy" ? views.deploy.nodeMetadata : views.system.nodeMetadata;
  // Only logical canvases carry `data-node-path` (#2917); the deploy view's
  // cards are addressed by their container key instead.
  const nodeMetadataByPath =
    activeView === "deploy" ? NO_NODE_METADATA_BY_PATH : views.system.nodeMetadataByPath;

  const outline = useOutline({
    activeView,
    deployTree: views.deploy.deployTree,
    organizations: views.org.organizations,
    resolvedSystems: views.system.resolvedSystems,
    systemNodeMetadata: views.system.nodeMetadata,
    orgPathIndex: views.orgPathIndex,
    dispatch,
    navigateViewPath,
  });

  const { handleEditorChange, handleFormat, handleTidyStyle, isStyleFile } = useEditorDocument({
    fs,
    currentFilePath,
    fileContent,
    dispatch,
    recompile,
  });

  const hasParseErrors = views.system.diagnostics.some((d) => d.severity === "error");

  const styleSource = useStyleSource(fileContent, currentFilePath ?? undefined, fs, sourceRevision);

  const { onPickEdgeDirection, styleTargetPath } = useEdgeDirectionWriter({
    fs,
    currentFilePath,
    fileContent,
    handleEditorChange,
    recompile,
  });

  const {
    getDrillDownSvg,
    allLayersSvg,
    orgAllLayersSvg,
    getOrgDrillDownSvg,
    getAllViewsSvg,
    exportAvailable,
    entityViewSvg,
    entityViewDiagnostics,
    hasEntityView,
  } = useViewSvg(
    fileContent,
    displayMode,
    styleSource,
    effectiveTheme,
    // Carry the system view's Group-by axis into the export / all-layers SVGs
    // and the live entity view, so group frames reach every surface for both
    // axes (#1879, #2033). Exports keep the full structure (no collapse) by
    // design. `groupByAxis` is the shared conversion, so a new axis value
    // passes through without editing this site (TPL-219 — the old
    // `=== "team"` hardcode here silently dropped the boundary axis).
    groupByAxis(views.system.groupBy),
    viewPath,
    // Same reason, same trap: an export that silently drops the overlay shows a
    // different diagram than the one the reader is looking at (#2174).
    views.system.selectedFacets,
    // The All-layers panel's SVG is built only while the panel is open (#2758).
    { allLayersOpen: isAllLayersOpen },
  );

  const hasSidebar = !!(sidebarHeaderContent || sidebarContent);
  const shellRef = useRef<HTMLDivElement | null>(null);
  const editorResize = useEditorWidth(shellRef);
  const showEditorResizeHandle = !hideEditor && !previewFocused;
  const hasExplicitEditorWidth = editorResize.editorWidth != null && !previewFocused && !hideEditor;
  const className = hideEditor
    ? "app serve-mode"
    : [
        "app-shell",
        hasSidebar ? "has-sidebar" : "",
        previewFocused ? "preview-focused" : "",
        hasExplicitEditorWidth ? "has-editor-width" : "",
        editorResize.isDragging ? "editor-dragging" : "",
      ]
        .filter(Boolean)
        .join(" ");
  const shellStyle: CSSProperties | undefined = hasExplicitEditorWidth
    ? ({ "--editor-w": `${editorResize.editorWidth}px` } as CSSProperties)
    : undefined;

  const toggleAllLayers = useCallback(() => setIsAllLayersOpen((v) => !v), []);
  const togglePreviewFocus = useCallback(() => setPreviewFocused((v) => !v), []);
  const toggleEntityView = useCallback(() => setIsEntityViewOpen((v) => !v), []);

  // Stable identity so it doesn't bust the preview-context memo every render
  // (the original kept this inside the memo, keyed on entryPath/fs).
  const onExportDrawio = useMemo(
    () => (entryPath ? (filename: string) => downloadDrawio(entryPath, fs, filename) : undefined),
    [entryPath, fs],
  );

  // Share (karasu-nest): flatten the whole project — resolving `import`s and
  // merging styles from `fs` — into a single self-contained payload. This reads
  // the same entry + fs the preview compiles from (auto-save keeps fs current),
  // so the share reflects exactly what's rendered. The getter is stable and
  // `hasKrsSource` only flips on the empty↔non-empty boundary, so neither
  // re-renders the preview on every keystroke. Falls back to the live single
  // file if synthesis fails (e.g. a transient parse error).
  const fileContentRef = useLatestRef(fileContent);
  const getShareBundle = useCallback(async (): Promise<SharePayload> => {
    if (entryPath) {
      try {
        return await synthesizeSharePayload(entryPath, fs);
      } catch (err) {
        // ImportResolver collects missing/circular/parse issues as diagnostics
        // rather than throwing, so this only fires on an unexpected synthesis
        // failure. The single-file fallback is a best effort and may be broken
        // for a multi-file project, so surface it rather than failing silently.
        // eslint-disable-next-line no-console
        console.warn("karasu-nest: share synthesis failed, sharing raw entry", err);
      }
    }
    return { krs: fileContentRef.current };
    // `fileContentRef` is a stable ref object (only `.current` is read here),
    // so listing it satisfies exhaustive-deps without changing memoization.
  }, [entryPath, fs, fileContentRef]);
  const hasKrsSource = fileContent.trim() !== "";

  const displayRoot = hostDisplayRoot ?? displayRootFor(projectRoot, entryPath);

  const previewContextValue = usePreviewContextValue({
    activeView,
    viewPath,
    selectedDeployBlockId,
    highlightedNodeId,
    highlightAttribute,
    nodeMetadata,
    nodeMetadataByPath,
    // Passed straight through (#2015 point 8) — `usePreviewContextValue`'s arg
    // types are `Pick<...ViewBundle, ...>` slices of these same bundles, so
    // the extra fields each bundle carries (e.g. `nodeMetadata`) are simply
    // ignored rather than needing to be hand-filtered out here.
    system: views.system,
    deploy: views.deploy,
    org: views.org,
    breadcrumbItems,
    orgBreadcrumbItems,
    nav,
    navigateActiveView,
    navigateViewPath,
    isAllLayersOpen,
    toggleAllLayers,
    getDrillDownSvg,
    allLayersSvg,
    orgAllLayersSvg,
    getOrgDrillDownSvg,
    getAllViewsSvg,
    // A bundle can be built: settled content that parses (a broken source has
    // nothing to export, as before, when the eager build simply failed).
    exportBundlesAvailable: exportAvailable && !hasParseErrors,
    previewFocused,
    togglePreviewFocus,
    onJumpToEditor: revealNode ?? (!hideEditor ? handleJumpToEditor : undefined),
    isOrgTreeViewOpen,
    toggleOrgTreeView,
    isTeamDependenciesOpen,
    toggleTeamDependencies,
    isEntityViewOpen,
    toggleEntityView,
    entityViewSvg,
    entityViewDiagnostics,
    hasEntityView,
    styleTargetPath: readOnly ? undefined : styleTargetPath,
    onPickEdgeDirection: readOnly ? undefined : onPickEdgeDirection,
    readOnly,
    onExportSvg: downloadSvg,
    onExportDrawio,
    hasKrsSource,
    getShareBundle,
    currentFilePath,
    displayRoot,
  });

  return (
    <div className={className} style={shellStyle} ref={shellRef}>
      {sidebarHeaderContent}
      {showEditorResizeHandle && (
        <div
          className="editor-preview-handle"
          onMouseDown={editorResize.onMouseDown}
          onDoubleClick={editorResize.onDoubleClick}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize editor and preview"
        />
      )}
      {!hideEditor && (
        <EditArea
          sidebarContent={sidebarContent}
          outlineContent={
            sidebarContent ? (
              <OutlineView
                nodes={outline.nodes}
                highlightedNodeId={highlightedNodeId}
                onSelectNode={outline.onSelectNode}
                onActivateNode={outline.onActivateNode}
              />
            ) : undefined
          }
          previewFocused={previewFocused}
          editorContent={
            <EditPane
              value={fileContent}
              currentFilePath={currentFilePath}
              onChange={handleEditorChange}
              onEditorReady={handleEditorReady}
              scopeLabel={scopeLabel}
              viewPath={viewPath}
              currentProjectId={currentProject?.id ?? null}
              resolvedSystems={views.system.resolvedSystems}
              organizations={views.org.organizations}
              ownerIndex={views.org.ownerIndex}
              onNavigateViewPath={navigateViewPath}
              onFormat={isStyleFile ? undefined : handleFormat}
              onTidyStyle={isStyleFile ? handleTidyStyle : undefined}
              hasParseErrors={hasParseErrors}
              displayMode={displayMode}
              onDisplayModeChange={nav.handleDisplayModeChange}
            />
          }
        />
      )}
      <DiagramViewShortcuts onActiveViewChange={navigateActiveView} />
      <PreviewFocusShortcut onToggle={togglePreviewFocus} />
      <PreviewProvider value={previewContextValue}>
        {compareSource && (
          <DiffModeBanner
            source={compareSource}
            snapshotManager={snapshotManager}
            currentPath={currentFilePath}
            swapped={diffSwapped}
            onExit={() => dispatch({ type: "SET_COMPARE_SOURCE", source: null })}
            onSwap={() => dispatch({ type: "TOGGLE_DIFF_SWAPPED" })}
            onViewPasted={onViewPasted}
          />
        )}
        <PreviewColumn />
      </PreviewProvider>
    </div>
  );
}
